
import { fromEvent as observableFromEvent } from 'rxjs'

import { mdiAlertCircleOutline } from '@mdi/js'
import Icon from '@mdi/react'
import AnyCard from 'components/Card'
import LoadingCard from 'components/Card/LoadingCard'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import EmptyFetchError from 'components/Fetch/FetchError'
import ScrollListener, { ScrollListenerHandle } from 'components/ScrollListener'
import { TIMERANGE_FILTER_DEFAULT } from 'config'
import useConditionalEffect from 'hooks/useConditionalEffect'
import { useElementSize } from 'hooks/useElementSize'
import {
  ALL_RANGE_FILTER,
  AnyContent,
  Article,
  ContentItem,
  LiveArticleData,
  MONTH_RANGE_FILTER,
  NEW_RANGE_FILTER,
  Photo,
  PHOTO_TYPE,
  RangeFilter,
  StatusIdea,
  TEXT_TYPE,
  THREE_DAYS_RANGE_FILTER,
  WEEK_RANGE_FILTER,
  YEAR_RANGE_FILTER
} from 'interfaces'
import { STOCK_CONTENT_PROVIDERS_ALL, StockContentProvider } from 'interfaces/Content/StockContentProvider'
import * as React from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { debounceTime } from 'rxjs/operators'

import { addLiveArticleData, setVisibleSelectableItems } from 'services/content'
import { SelectableContent } from 'services/content/state'
import { dedupeArticles, dedupeMixedArticles, SortBy } from 'services/content/util'
import { checkFeatureAvailability, contentTypeFeatureMap, rangeFeatureMap } from 'services/product'
import { message } from 'services/snackbar'
import {
  ALL_FILTER,
  arrayToRecord,
  ARTICLE_TYPE,
  ContentType,
  FilterType,
  STATUS_TYPE,
  STOCK_ALL_FILTER,
  STOCK_VIDEO_TYPE,
  VIDEO_TYPE
} from 'shared'
import { StoreThunkDispatch } from 'store/state'
import { MS_IN_DAY } from 'utils/constants'
import { resetScroll } from 'utils/dom'
import { getLastUsedSortBy, setLastUsedFilters, setLastUsedSortBy } from 'utils/filter-store'
import styles from './ContentGridView.pcss'
import ContentGridViewError from './ContentGridViewError'
import { ContentGridViewFilters } from './ContentGridViewFilters'
import { ContentGridViewProps, GRID_BREAKPOINTS } from './types'
import { ContentFetchParams, useContentFetch } from './useContentFetch'

const timeRangeMs: Record<string, number> = {
  /* eslint-disable no-magic-numbers */
  [NEW_RANGE_FILTER]: MS_IN_DAY * 2,
  [THREE_DAYS_RANGE_FILTER]: MS_IN_DAY * 3,
  [WEEK_RANGE_FILTER]: MS_IN_DAY * 7,
  [MONTH_RANGE_FILTER]: MS_IN_DAY * 30,
  [YEAR_RANGE_FILTER]: MS_IN_DAY * 365,
  [ALL_RANGE_FILTER]: MS_IN_DAY * 365 * 3
  /* eslint-enable no-magic-numbers */
}

// eslint-disable-next-line no-magic-numbers
const LOADING_CARD_SIZES = [400, 260, 320, 380, 280, 320, 200, 320, 400, 260, 320, 380, 280, 320, 200, 320]

export const ContentGridView = memo((props: ContentGridViewProps) => {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const { filter, range, contentTypes, sourceId, isStream, onItemShared } = props
  const [errorOffset, setErrorOffset] = useState(0)
  const [query, setQuery] = useState('')

  const [brokenPhotoIds, setBrokenPhotoIds] = useState<string[]>([])
  const {
    content,
    error,
    clearError,
    isFetching,
    fetchParams,
    loadNextPage,
    setFetchParams
  } = useContentFetch({
    contentType: filter,
    sourceId,
    page: 0,
    sourceType: isStream ? 'stream' : 'feed',
    timeRange: props.range || TIMERANGE_FILTER_DEFAULT,
    sortBy: getLastUsedSortBy() as any || SortBy.Engagement,
    stockContentProvider: STOCK_CONTENT_PROVIDERS_ALL
  })
  const [gridContainerRef, { height }] = useElementSize<HTMLDivElement>()
  const [showLiveContentWarning, setShowLiveContentWarning] = useState(false)
  const scrollListenerRef = useRef<ScrollListenerHandle>(null)

  useEffect(() => {
    if (error?.source?.response?.status === 'error' && error.source.response.message) {
      dispatch(message(error.source.response.message, 'error', NOTIFICATION_DURATION_LONG))
    }
  }, [dispatch, error])

  useEffect(() => {
    const contentDate = content[0]?.createdAt ? new Date(content[0].createdAt).getTime() : null
    let isContentOlderThanRange = false
    if (!isFetching && contentDate && range) {
      if (Date.now() - contentDate > timeRangeMs[range]) {
        isContentOlderThanRange = true
      }
    }
    setShowLiveContentWarning(isContentOlderThanRange)
  }, [content, range, isFetching])

  useEffect(() => {
    const padding = 80
    setErrorOffset(content.length > 0 ? height + padding : 0)
  }, [height, content.length])

  useConditionalEffect(([currentTimeRange, currentContentTypes]) => {
    const nextParams: Partial<ContentFetchParams> = { sourceId, contentType: filter }
    if (range && range !== currentTimeRange) {
      nextParams.timeRange = range
      checkRangeLimits(range)
    }
    if (filter !== ALL_FILTER && filter !== STOCK_ALL_FILTER && currentContentTypes.indexOf(filter as any) === -1) {
      checkTypeLimits(filter as any)
    }
    setFetchParams(nextParams)
    setQuery('')
  }, [range, filter, sourceId, setFetchParams], [fetchParams.timeRange, contentTypes])

  const checkRangeLimits = (range: RangeFilter, skipUpgradePopup?: boolean) => {
    // NEW and WEEK are available to all, skip check
    // Skip check for Stock content.
    const rangeAllowed = props.noContentRangeCheck
      || (range === NEW_RANGE_FILTER || filter.startsWith('stock'))
      || dispatch(checkFeatureAvailability(
        rangeFeatureMap[range],
        skipUpgradePopup,
        () => setLastUsedFilters(filter, THREE_DAYS_RANGE_FILTER)
      ))

    if (!rangeAllowed && skipUpgradePopup) {
      onRangeChange(THREE_DAYS_RANGE_FILTER)
    }
  }

  const checkTypeLimits = (type: ContentType, skipUpgradePopup?: boolean) => {
    const typeAllowed = props.noContentTypeCheck
      || type === ALL_FILTER as string
      || dispatch(checkFeatureAvailability(contentTypeFeatureMap[type], skipUpgradePopup))

    if (!typeAllowed && skipUpgradePopup) {
      onFilterChange(ARTICLE_TYPE)
    }
  }

  useEffect(() => {
    if (![VIDEO_TYPE, STOCK_VIDEO_TYPE, STATUS_TYPE].includes(filter)) {
      dispatch(setVisibleSelectableItems(arrayToRecord(content as SelectableContent[] || [], 'id')))
    }
  }, [content, filter, dispatch])

  useEffect(() => {
    const windowResize$ = observableFromEvent(window, 'resize').pipe(
      debounceTime(250)) // eslint-disable-line no-magic-numbers
      .subscribe(() => {
        if (scrollListenerRef.current) {
          scrollListenerRef.current.emitScroll()
        }
      })
    return () => {
      windowResize$.unsubscribe()
      dispatch(setVisibleSelectableItems({}))
    }
  }, [dispatch])

  const onSortByChange = (value: SortBy) => {
    resetScroll()
    setLastUsedSortBy(value)
    setFetchParams({ sortBy: value })
    setQuery('')
  }

  const onRangeChange = (r: RangeFilter) => {
    resetScroll()
    if (props.onRangeChanged && props.onRangeChanged(r)) {
      return
    }
    setQuery('')
  }

  const onFilterChange = (f: FilterType) => {
    props.onFilterChange(f)
    resetScroll()
  }

  const onStockSourceChange = (s: StockContentProvider) => {
    setFetchParams({ stockContentProvider: s })
    setQuery('')
  }

  const scrollElement = useMemo(() => {
    return props.scroll?.element || document.getElementsByTagName('main')[0]
  }, [props.scroll])


  const onCompose = useCallback((item: ContentItem) => {
    if (onItemShared) {
      return onItemShared(item as AnyContent)
    }
  }, [onItemShared])

  const onImageLoadingFailed = React.useCallback((item: ContentItem) => {
    if (item.hash) {
      setBrokenPhotoIds(current => current.includes(item.hash) ? current : current.concat(item.hash))
    }
  }, [])

  const onLiveArticleDataFetched = useCallback((id: string, data: LiveArticleData) => {
    dispatch(addLiveArticleData({ [id]: { id, ...data } }))
  }, [dispatch])

  const contentCards = useMemo(() => {
    const brokenPhotos = brokenPhotoIds
    if (content.length === 0) {
      return []
    }

    let items: any = content.filter(item => brokenPhotos.indexOf((item as any).hash) === -1)
    if (filter === ARTICLE_TYPE) {
      items = dedupeArticles(items as Article[])
    }

    if (filter === ALL_FILTER) {
      items = dedupeMixedArticles(items)
    }

    if (query) {
      const q = query.trim().toLowerCase()
      switch (filter) {
        case ARTICLE_TYPE:
        case VIDEO_TYPE:
          items = items.filter((item: Article) => {
            return item.title?.toLowerCase().includes(q)
              || item.description?.toLowerCase().includes(q)
              || item.status?.toLowerCase().includes(q)
          })
          break
        case PHOTO_TYPE:
          items = items.filter((item: Photo) => item.status?.toLowerCase().includes(q))
          break
        case STATUS_TYPE:
        case TEXT_TYPE:
          items = items.filter((item: StatusIdea) => {
            return item.status?.toLowerCase().includes(q) || item.author?.toLowerCase().includes(q)
          })
          break
      }
    }

    return items.map((item: any) => {
      const textCardProps = {
        onPin: props.onItemPinned,
        onSourceClick: props.onSourceClick as any,
        onFeedClick: props.onFeedClick
      }

      return (
        <AnyCard
          key={item.id}
          children={item}
          content={item}
          width={props.itemWidth}
          square={false}
          skipFeatureChecks={props.skipContentCardsFeatureChecks}
          onCompose={onCompose}
          onClick={props.onContentItemClick}
          onImageLoadingFailed={onImageLoadingFailed}
          onLiveArticleDataFetched={onLiveArticleDataFetched}
          {...(item.type === ARTICLE_TYPE ? textCardProps : {})}
        />
      )
    })
  }, [
    brokenPhotoIds,
    content, filter,
    props.onItemPinned,
    props.onSourceClick,
    props.onFeedClick,
    props.itemWidth,
    props.skipContentCardsFeatureChecks,
    props.onContentItemClick,
    query,
    onCompose,
    onImageLoadingFailed,
    onLiveArticleDataFetched
  ])

  const loadingCards = React.useMemo(() => {
    const shouldRenderLoadingCards = isFetching || (content.length === 0 && !props.hideEmptyCards)
    if (!shouldRenderLoadingCards) {
      return []
    }
    return LOADING_CARD_SIZES.map((height, index) => {
      return (
        <LoadingCard
          key={`loading-card-${index}`}
          width={props.itemWidth}
          height={height}
          active={isFetching}
        />
      )
    })
  }, [isFetching, content.length, props.hideEmptyCards, props.itemWidth])

  const withSearch = Boolean(query.trim())
  const showNoMoreContent = !isFetching && !withSearch && error instanceof EmptyFetchError && content.length > 0

  return (
    <div className={`${styles.view} ${props.className || ''}`} data-testid="content-grid">
      {!props.hideFilters && (
        <ContentGridViewFilters
          filter={filter}
          sortBy={fetchParams.sortBy}
          range={fetchParams.timeRange}
          contentTypes={contentTypes}
          hideRangeFilter={props.hideRangeFilter}
          hideSorting={props.hideSorting}
          className={props.filtersContainerClassName}
          feedType={props.feedType}
          stockContentSource={fetchParams.stockContentProvider}
          query={query}
          disableSearch={isFetching || content.length === 0}
          onQueryChange={(value: string) => { setQuery(value); if (!value.trim()) { clearError() } }}
          onFilterChange={onFilterChange as any}
          onRangeChange={onRangeChange}
          onStockSourceChange={onStockSourceChange}
          onSortByChange={onSortByChange}
        />
      )}
      {showLiveContentWarning && (
        <div className={styles.liveContentWarning}>
          <Icon path={mdiAlertCircleOutline} size="20px" />
          <span>The content below is the most recent available</span>
        </div>
      )}
      {contentCards.length + loadingCards.length > 0 && (
        <ScrollListener
          ref={scrollListenerRef}
          className={styles.bricks}
          disabled={isFetching}
          emitTreshold={250}
          onScrollDebounceTime={50}
          emitInitial={false}
          useWindow={props.scroll?.useWindow}
          scrollElement={scrollElement}
          onScroll={loadNextPage}
        >
          <div ref={gridContainerRef} data-testid="grid">
            <ResponsiveMasonry columnsCountBreakPoints={GRID_BREAKPOINTS}>
              <Masonry gutter="12px" className={styles.masonry}>
                {contentCards.concat(loadingCards)}
              </Masonry>
            </ResponsiveMasonry>
          </div>
        </ScrollListener>
      )}
      {showNoMoreContent && (
        <div className={styles.noMoreContent}>
          {intl.formatMessage({ id: 'content.msg-no-content-for-range' })}
        </div>
      )}
      {!isFetching && error && !withSearch && !showNoMoreContent && (
        <ContentGridViewError
          filter={filter}
          range={range as any}
          hasItems={content.length > 0}
          error={error}
          offset={errorOffset}
          isStream={props.isStream}
          errorElement={props.errorElement}
          isFavoritesView={props.favoritesMode}
        />
      )}
      {!isFetching && withSearch && contentCards.length === 0 && (
        <div className={styles.noResults}>
          No results found.
        </div>
      )}
    </div>
  )
})
